import { computed, createModel, signal } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import type { StickyFilterSliceEvents, StickyFilterSliceSignals } from 'src/types/EventBus';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';
import type { EventBus } from 'src/types/EventBus/EventBus';
import { z } from 'zod';

export const stickyFilterStateSchema = z.object({
    filter: z.string().nullable().default(null),
});

export type StickyFilterState = z.infer<typeof stickyFilterStateSchema>;

export type StickyFilterModelEventBus = EventBus<StickyFilterSliceSignals, StickyFilterSliceEvents>;

interface StickyFilterModelInit {
    persisted: StickyFilterState;
}

export interface StickyFilterModelShape {
    readonly dirty: ReadonlySignal<boolean>;
    readonly eventBus: StickyFilterModelEventBus;
    toJSON(): StickyFilterState;
    markClean(): void;
}

export const StickyFilterModel: ModelConstructor<StickyFilterModelShape, [StickyFilterModelInit]> = createModel<
    StickyFilterModelShape,
    [StickyFilterModelInit]
>((init: StickyFilterModelInit) => {
    const persistedFilter = signal<string | null>(init.persisted.filter);
    const cleanFilter = signal<string | null>(init.persisted.filter);
    const stickyFilterValue = signal(init.persisted.filter ?? '');
    const isStickyFilterSticky = signal(Boolean(init.persisted.filter));

    const eventBus: StickyFilterModelEventBus = EventBusBuilder.create()
        .addSignal('widget::stickyFilterValue', (): ReadonlySignal<string> => computed(() => stickyFilterValue.value))
        .addSignal(
            'widget::isStickyFilterSticky',
            (): ReadonlySignal<boolean> => computed(() => isStickyFilterSticky.value),
        )
        .addEvent('widget::updateStickyFilter', (payload) => {
            stickyFilterValue.value = payload.filter;

            if (isStickyFilterSticky.value) {
                persistedFilter.value = payload.filter;
            }
        })
        .addEvent('widget::toggleStickyFilter', () => {
            const nextIsSticky = !isStickyFilterSticky.value;
            isStickyFilterSticky.value = nextIsSticky;
            persistedFilter.value = nextIsSticky ? stickyFilterValue.value || null : null;
        })
        .build();

    const dirty: ReadonlySignal<boolean> = computed(() => persistedFilter.value !== cleanFilter.value);

    return {
        dirty,
        eventBus,
        toJSON(): StickyFilterState {
            return { filter: persistedFilter.value };
        },
        markClean(): void {
            cleanFilter.value = persistedFilter.value;
        },
    };
});

export type StickyFilterModel = Model<StickyFilterModelShape>;
